import {guestRequest} from './guestApi';
import {TimeFormatContext,preserveTimePreference} from './dateTime';
import { db, workspaceAccount } from './db';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { FeaturePrivilegeKey, Privilege, PrivilegeMap, Role, RoleDefinition, UserAccount } from '../types.js';
import {
  resolveCurrentUser,
  resolvePrivilegesForUser,
  hasGoogleClientId,
  loadUsers,
  setCurrentUserEmail,
  beginGoogleRedirect,
  signOut,
  upsertUser,
  setRolePrivileges,
  loadRoleDefinitions,
  inviteUser,
  getAuthToken,
  deleteUser,
  fetchCurrentAccount,
} from './auth.js';

interface AuthContextValue {
  user: UserAccount | null;
  idToken: string | null;
  users: UserAccount[];
  roles: RoleDefinition[];
  privileges: PrivilegeMap;
  loading: boolean;
  mutating: boolean;
  error: string | null;
  googleClientReady: boolean;
  signIn: () => Promise<void>;
  signOut: () => void;
  refreshUsers: () => Promise<void>;
  refreshRoles: () => Promise<void>;
  refreshAccount: () => Promise<void>;
  saveTimePreference: (command:{timeFormat:'12h'|'24h';revision:number}) => Promise<void>;
  persistUser: (user: UserAccount & {create_only?:boolean}) => Promise<void>;
  updateRole: (email: string, role: Role) => Promise<void>;
  updateRolePrivilege: (role: Role, key: FeaturePrivilegeKey, privilege: Privilege) => Promise<void>;
  saveRolePrivileges: (role: Role, privileges: PrivilegeMap) => Promise<void>;
  invite: (email: string, role: Role | Role[], farmIds?: string[]) => Promise<UserAccount | null>;
  deleteUser: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUserState] = useState<UserAccount | null>(null);
  const setUser=(next:React.SetStateAction<UserAccount|null>)=>setUserState(previous=>preserveTimePreference(previous,typeof next==='function'?next(previous):next));
  const [idToken, setIdToken] = useState<string | null>(getAuthToken());
  const [users, setUsers] = useState<UserAccount[]>([]);
  const [roles, setRoles] = useState<RoleDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [googleClientReady] = useState<boolean>(hasGoogleClientId());

  const privileges = useMemo(() => resolvePrivilegesForUser(user, roles), [user, roles]);

  const refreshUsers = async () => {
    const updated = await loadUsers();
    setUsers(updated);
    if (user?.email) {
      const refreshed = updated.find((u) => u.email === user.email);
      if (refreshed) setUser(refreshed);
    }
  };

  const refreshRoles = async () => setRoles(await loadRoleDefinitions());
  const refreshAccount = async () => {
    const database = db;
    const account = await fetchCurrentAccount();
    if (database !== db) return;
    await database.transaction('rw',database.workspaceMeta,async()=>{const cached=await database.workspaceMeta.get('profile');await database.workspaceMeta.put({key:'profile',value:preserveTimePreference(cached?.value,account)});});
    setUser(account);
  };

  const saveTimePreference = async (command:{timeFormat:'12h'|'24h';revision:number}) => {
    const database=db,email=user?.email;
    if(!email)throw new Error('Sign in to change your time preference.');
    const result=await guestRequest<{time_format:'12h'|'24h';time_format_revision:number}>('/guest/time-preference','PUT',command);
    if(database!==db)throw new Error('Account changed. Reopen preferences.');
    await database.transaction('rw',database.workspaceMeta,async()=>{
      const cached=await database.workspaceMeta.get('profile');
      if(cached?.value?.email===email&&Number(cached.value.time_format_revision||0)<=result.time_format_revision)await database.workspaceMeta.put({key:'profile',value:{...cached.value,...result}});
    });
    if(database!==db)return;
    setUser(previous=>previous?.email===email&&Number(previous.time_format_revision||0)<=result.time_format_revision?{...previous,...result}:previous);
  };

  const signIn = async () => {
    setLoading(true);
    setError(null);
    try {
      await beginGoogleRedirect();
    } catch (err: any) {
      setError(err?.message || 'Failed to sign in');
      setLoading(false);
    } finally {
      // Redirect flow will leave the page; loading state will reset on return.
    }
  };

  const handleSignOut = () => {
    signOut();
    setUser(null);
    setIdToken(null);
  };

  const persistUser = async (updatedUser: UserAccount) => {
    setMutating(true);
    setError(null);
    try {
      const saved = await upsertUser(updatedUser);
      await refreshUsers();
      if (user?.email === updatedUser.email) {
        setUser({ ...saved });
        setCurrentUserEmail(updatedUser.email);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to update user');
      throw err;
    } finally {
      setMutating(false);
    }
  };

  const updateRole = async (email: string, role: Role) => {
    const current = users.find((u) => u.email === email);
    if (!current) return;
    await persistUser({ ...current, role, roles:[role] });
  };

  const updateRolePrivilege = async (role: Role, key: FeaturePrivilegeKey, privilege: Privilege) => {
    setMutating(true);
    setError(null);
    try {
      const current = roles.find((r) => r.role === role)?.privileges;
      if (!current) throw new Error('Role definition is unavailable. Refresh and try again.');
      const updated = { ...current, [key]: privilege };
      await setRolePrivileges(role, updated);
      await refreshRoles();
    } catch (err: any) {
      setError(err?.message || 'Failed to update privileges');
      throw err;
    } finally {
      setMutating(false);
    }
  };

  const invite = async (email: string, role: Role | Role[], farmIds: string[] = []) => {
    try {
      setMutating(true);
      setError(null);
      const created = await inviteUser(email, role, farmIds);
      await refreshUsers();
      return created;
    } catch (err) {
      setError((err as Error)?.message || 'Failed to invite user');
      return null;
    } finally {
      setMutating(false);
    }
  };

  const saveRolePrivileges = async (role: Role, privileges: PrivilegeMap) => {
    setMutating(true); setError(null);
    try { await setRolePrivileges(role, privileges); await refreshRoles(); }
    catch (err:any) { setError(err?.message || 'Failed to update permissions'); throw err; }
    finally { setMutating(false); }
  };

  const handleDeleteUser = async (email: string) => {
    setMutating(true);
    setError(null);
    try {
      await deleteUser(email);
      await refreshUsers();
      if (user?.email === email) {
        setCurrentUserEmail(null);
        setUser(null);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to delete user');
      throw err;
    } finally {
      setMutating(false);
    }
  };

  useEffect(() => {
    let active = true;
    let generation = 0;
    const bootstrap = async () => {
      const version = ++generation;
      const database = db;
      const current = () => active && version === generation && database === db;
      setUser(previous => previous?.email?.trim().toLowerCase() === workspaceAccount ? previous : null);
      setUsers([]); setRoles([]); setIdToken(getAuthToken());
      try {
        const [profile, savedRoles] = await Promise.all([database.workspaceMeta.get('profile'), database.workspaceMeta.get('roles')]);
        if (!current()) return;
        if (profile?.value) {
          setUser(profile.value); setUsers([profile.value]);
          setRoles(savedRoles?.value || []); setLoading(false);
        }
        const results = await Promise.allSettled([loadUsers(), loadRoleDefinitions(), resolveCurrentUser(true)]);
        if (!current()) return;
        if (results[0].status === 'fulfilled') setUsers(results[0].value);
        if (results[1].status === 'fulfilled') setRoles(results[1].value);
        if (results[2].status === 'fulfilled') setUser(results[2].value);
        const failed = results.find(result => result.status === 'rejected');
        if (failed?.status === 'rejected' && !profile?.value) setError(failed.reason?.message || 'Could not load account');
      } finally { if (current()) setLoading(false); }
    };
    void bootstrap();
    const handler = () => { void bootstrap(); };
    window.addEventListener('sonata-auth-changed', handler);
    const foreground = () => { if(navigator.onLine && document.visibilityState==='visible') void bootstrap(); };
    window.addEventListener('online',foreground);
    window.addEventListener('focus',foreground);
    return () => { active = false; window.removeEventListener('sonata-auth-changed', handler); window.removeEventListener('online',foreground);window.removeEventListener('focus',foreground); };
  }, []);

  const value: AuthContextValue = {
    user,
    idToken,
    users,
    roles,
    privileges,
    loading,
    mutating,
    error,
    googleClientReady,
    signIn,
    signOut: handleSignOut,
    refreshUsers,
    refreshRoles,
    refreshAccount,
    saveTimePreference,
    persistUser,
    updateRole,
    updateRolePrivilege,
    saveRolePrivileges,
    invite,
    deleteUser: handleDeleteUser,
  };

  return <AuthContext.Provider value={value}><TimeFormatContext.Provider value={user?.time_format==='24h'?'24h':'12h'}>{children}</TimeFormatContext.Provider></AuthContext.Provider>;
};
